import { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Pencil, Plus, Trash2, UserPlus, X } from 'lucide-react';
import dayjs from 'dayjs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/shared/DatePicker';
import { t } from '@/i18n';
import { useMileageApi } from '@/services/api';
import type { VehicleListItem, VehicleDriverItem } from '@ip/shared-types';

const COLUMN_COUNT = 6;

interface VehicleManagementProps {
  companyNip: string;
  vehicles: VehicleListItem[];
  onVehiclesChanged: () => void;
}

interface VehicleFormState {
  displayName: string;
  registrationNumber: string;
  initialOdometer: string;
  initialOdometerDate: Date | null;
  isActive: boolean;
  notes: string;
}

interface DriverFormState {
  fullName: string;
  isEmployee: boolean;
  isDefault: boolean;
}

const EMPTY_VEHICLE_FORM: VehicleFormState = {
  displayName: '',
  registrationNumber: '',
  initialOdometer: '0',
  initialOdometerDate: null,
  isActive: true,
  notes: '',
};

const EMPTY_DRIVER_FORM: DriverFormState = {
  fullName: '',
  isEmployee: true,
  isDefault: false,
};

export function VehicleManagement({ companyNip, vehicles, onVehiclesChanged }: VehicleManagementProps) {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [driverModalOpen, setDriverModalOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<VehicleListItem | null>(null);
  const [driverVehicleId, setDriverVehicleId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<VehicleFormState>(EMPTY_VEHICLE_FORM);
  const [driverForm, setDriverForm] = useState<DriverFormState>(EMPTY_DRIVER_FORM);

  const { createVehicle, updateVehicle, deleteVehicle, addDriver, deleteDriver } = useMileageApi();

  const setField = <K extends keyof VehicleFormState>(field: K, value: VehicleFormState[K]) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleAdd = () => {
    setEditingVehicle(null);
    setForm({ ...EMPTY_VEHICLE_FORM, initialOdometerDate: new Date() });
    setEditModalOpen(true);
  };

  const handleEdit = (vehicle: VehicleListItem) => {
    setEditingVehicle(vehicle);
    setForm({
      displayName: vehicle.displayName,
      registrationNumber: vehicle.registrationNumber,
      initialOdometer: String(vehicle.initialOdometer),
      initialOdometerDate: vehicle.initialOdometerDate ? dayjs(vehicle.initialOdometerDate).toDate() : null,
      isActive: vehicle.isActive,
      notes: vehicle.notes || '',
    });
    setEditModalOpen(true);
  };

  const validateForm = (): boolean => {
    if (!form.displayName.trim()) {
      toast.error(t('employees.validation.required', { label: t('mileage.vehicles.displayName') }));
      return false;
    }
    if (!form.registrationNumber.trim()) {
      toast.error(t('employees.validation.required', { label: t('mileage.vehicles.registrationNumber') }));
      return false;
    }
    const odometer = Number(form.initialOdometer);
    if (form.initialOdometer === '' || Number.isNaN(odometer) || odometer < 0) {
      toast.error(t('employees.validation.required', { label: t('mileage.vehicles.initialOdometer') }));
      return false;
    }
    if (!form.initialOdometerDate) {
      toast.error(t('employees.validation.required', { label: t('mileage.vehicles.initialOdometerDate') }));
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setSaving(true);
      const base = {
        displayName: form.displayName,
        registrationNumber: form.registrationNumber,
        initialOdometer: Number(form.initialOdometer),
        initialOdometerDate: dayjs(form.initialOdometerDate).format('YYYY-MM-DD'),
      };
      if (editingVehicle) {
        await updateVehicle(companyNip, editingVehicle.id, {
          ...base,
          isActive: form.isActive,
          notes: form.notes,
        });
      } else {
        await createVehicle(companyNip, { ...base, notes: form.notes || undefined });
      }
      toast.success(t('common.save') + ' OK');
      setEditModalOpen(false);
      onVehiclesChanged();
    } catch (error) {
      console.error('Failed to save vehicle:', error);
      toast.error(t('errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (vehicleId: string) => {
    try {
      await deleteVehicle(companyNip, vehicleId);
      toast.success(t('common.delete') + ' OK');
      onVehiclesChanged();
    } catch (error) {
      console.error('Failed to delete vehicle:', error);
      toast.error(t('errors.deleteFailed'));
    }
  };

  const handleAddDriver = (vehicleId: string) => {
    setDriverVehicleId(vehicleId);
    setDriverForm(EMPTY_DRIVER_FORM);
    setDriverModalOpen(true);
  };

  const handleSaveDriver = async () => {
    if (!driverForm.fullName.trim()) {
      toast.error(t('employees.validation.required', { label: t('mileage.vehicles.driverName') }));
      return;
    }
    try {
      setSaving(true);
      await addDriver(companyNip, driverVehicleId, driverForm);
      toast.success(t('common.save') + ' OK');
      setDriverModalOpen(false);
      onVehiclesChanged();
    } catch (error) {
      console.error('Failed to add driver:', error);
      toast.error(t('errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDriver = async (vehicleId: string, driverId: string) => {
    try {
      await deleteDriver(companyNip, vehicleId, driverId);
      onVehiclesChanged();
    } catch (error) {
      console.error('Failed to delete driver:', error);
      toast.error(t('errors.deleteFailed'));
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <span className="font-medium">{t('mileage.vehicles.title')}</span>
        <Button size="sm" onClick={handleAdd}>
          <Plus className="size-4" />
          {t('mileage.vehicles.add')}
        </Button>
      </div>

      <div className="bg-card overflow-x-auto rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="text-muted-foreground w-[180px]">
                {t('mileage.vehicles.displayName')}
              </TableHead>
              <TableHead className="text-muted-foreground w-[130px]">
                {t('mileage.vehicles.registrationNumber')}
              </TableHead>
              <TableHead className="text-muted-foreground w-[150px]">
                {t('mileage.vehicles.initialOdometer')}
              </TableHead>
              <TableHead className="text-muted-foreground w-[250px]">
                {t('mileage.vehicles.drivers')}
              </TableHead>
              <TableHead className="text-muted-foreground w-[70px] text-center">
                {t('mileage.vehicles.isActive')}
              </TableHead>
              <TableHead className="text-muted-foreground w-[100px]">
                {t('common.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {vehicles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="text-muted-foreground h-24 text-center">
                  {t('mileage.vehicles.noVehicles')}
                </TableCell>
              </TableRow>
            ) : (
              vehicles.map(vehicle => (
                <TableRow key={vehicle.id}>
                  <TableCell className="py-2">{vehicle.displayName}</TableCell>
                  <TableCell className="py-2">
                    <Badge variant="outline">{vehicle.registrationNumber}</Badge>
                  </TableCell>
                  <TableCell className="py-2 whitespace-nowrap">
                    {vehicle.initialOdometer.toLocaleString()} km ({vehicle.initialOdometerDate})
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="flex flex-wrap items-center gap-1">
                      {vehicle.drivers.map((d: VehicleDriverItem) => (
                        <Badge key={d.id} variant={d.isDefault ? 'default' : 'secondary'} className="gap-1">
                          {d.fullName}{!d.isEmployee && ' (ext)'}
                          <button
                            type="button"
                            aria-label={t('common.delete')}
                            className="hover:opacity-70"
                            onClick={() => handleDeleteDriver(vehicle.id, d.id)}
                          >
                            <X className="size-3" />
                          </button>
                        </Badge>
                      ))}
                      <Button
                        variant="outline"
                        size="icon"
                        className="size-6 border-dashed"
                        onClick={() => handleAddDriver(vehicle.id)}
                      >
                        <UserPlus className="size-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="py-2 text-center">
                    {vehicle.isActive ? (
                      <Badge
                        variant="outline"
                        className="border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                      >
                        Yes
                      </Badge>
                    ) : (
                      <Badge variant="outline">No</Badge>
                    )}
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        onClick={() => handleEdit(vehicle)}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-destructive size-8">
                            <Trash2 className="size-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this vehicle?</AlertDialogTitle>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-destructive text-white hover:bg-destructive/90"
                              onClick={() => handleDelete(vehicle.id)}
                            >
                              {t('common.delete')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Vehicle edit dialog */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>
              {editingVehicle ? t('mileage.vehicles.editTitle') : t('mileage.vehicles.add')}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="vehicle-display-name">{t('mileage.vehicles.displayName')}</Label>
              <Input
                id="vehicle-display-name"
                placeholder="e.g. Audi A4 Avant"
                value={form.displayName}
                onChange={e => setField('displayName', e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="vehicle-registration">{t('mileage.vehicles.registrationNumber')}</Label>
              <Input
                id="vehicle-registration"
                placeholder="e.g. KR 12345"
                value={form.registrationNumber}
                onChange={e => setField('registrationNumber', e.target.value)}
              />
            </div>
            <div className="flex gap-4">
              <div className="flex w-[150px] flex-col gap-1.5">
                <Label htmlFor="vehicle-odometer">{t('mileage.vehicles.initialOdometer')}</Label>
                <Input
                  id="vehicle-odometer"
                  type="number"
                  min={0}
                  value={form.initialOdometer}
                  onChange={e => setField('initialOdometer', e.target.value)}
                />
              </div>
              <div className="flex w-[180px] flex-col gap-1.5">
                <Label>{t('mileage.vehicles.initialOdometerDate')}</Label>
                <DatePicker
                  value={form.initialOdometerDate}
                  onChange={date => setField('initialOdometerDate', date)}
                />
              </div>
            </div>
            {editingVehicle && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="vehicle-active">{t('mileage.vehicles.isActive')}</Label>
                <Switch
                  id="vehicle-active"
                  checked={form.isActive}
                  onCheckedChange={checked => setField('isActive', checked)}
                />
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="vehicle-notes">{t('mileage.vehicles.notes')}</Label>
              <Textarea
                id="vehicle-notes"
                rows={2}
                value={form.notes}
                onChange={e => setField('notes', e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={saving} onClick={handleSave}>
              {saving && <Loader2 className="size-4 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Driver add dialog */}
      <Dialog open={driverModalOpen} onOpenChange={setDriverModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{t('mileage.vehicles.addDriver')}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="driver-full-name">{t('mileage.vehicles.driverName')}</Label>
              <Input
                id="driver-full-name"
                placeholder="Jan Kowalski"
                value={driverForm.fullName}
                onChange={e => setDriverForm(prev => ({ ...prev, fullName: e.target.value }))}
              />
            </div>
            <div className="flex gap-6">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="driver-is-employee">{t('mileage.vehicles.isEmployee')}</Label>
                <Switch
                  id="driver-is-employee"
                  checked={driverForm.isEmployee}
                  onCheckedChange={checked => setDriverForm(prev => ({ ...prev, isEmployee: checked }))}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="driver-is-default">{t('mileage.vehicles.isDefault')}</Label>
                <Switch
                  id="driver-is-default"
                  checked={driverForm.isDefault}
                  onCheckedChange={checked => setDriverForm(prev => ({ ...prev, isDefault: checked }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDriverModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={saving} onClick={handleSaveDriver}>
              {saving && <Loader2 className="size-4 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
